import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wallet, ShoppingBag, TrendingUp, CalendarClock, ArrowRight, Receipt } from "lucide-react";
import B2BLayout from "../business/B2BLayout";
import B2BQuickReorder from "../business/B2BQuickReorder";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const StatCard = ({ icon: Icon, label, value, sub, tone = "default" }) => (
    <div className={`rounded-2xl border bg-white p-5 shadow-sm ${tone === "warn" ? "border-amber-200" : "border-zinc-100"}`} data-testid={`b2b-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <p className="mt-2 text-2xl font-semibold text-slate-900">{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
);

const BusinessDashboard = () => {
    const [orders, setOrders] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        Promise.all([
            api.get("/b2b/orders", { params: { page_size: 5 } }),
            api.get("/b2b/invoices", { params: { page_size: 5 } }),
        ]).then(([oRes, iRes]) => {
            if (!mounted) return;
            setOrders(oRes.data.items || []);
            setInvoices(iRes.data.items || []);
        }).catch(() => {}).finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);

    return (
        <B2BLayout title="Dashboard" subtitle="B2B portal">
            {(account) => {
                if (!account) return <div className="h-40 skeleton rounded-3xl" />;
                if (account.status !== "approved") {
                    return (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
                            <p className="font-semibold">Account not approved yet.</p>
                            <p className="text-sm">Status: {account.status}. <Link to="/business/pending" className="underline">View details</Link></p>
                        </div>
                    );
                }
                const credit = Number(account.credit_limit || 0);
                const outstanding = Number(account.current_outstanding || 0);
                const available = Math.max(0, credit - outstanding);
                const nextDue = invoices.find((i) => i.status === "issued");
                return (
                    <div className="space-y-6" data-testid="b2b-dashboard">
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <StatCard icon={Wallet} label="Credit limit" value={inr(credit)} sub={`${account.credit_terms_days}-day terms · ${account.discount_tier} (${account.discount_tier === 'tier1' ? '5%' : account.discount_tier === 'tier2' ? '10%' : '15%'} off)`} />
                            <StatCard icon={TrendingUp} label="Outstanding" value={inr(outstanding)} sub={`${available > 0 ? inr(available) + " available" : "Credit exhausted"}`} tone={outstanding / Math.max(1, credit) > 0.8 ? "warn" : "default"} />
                            <StatCard icon={ShoppingBag} label="Orders (90d)" value={orders.length} sub={orders.filter(o => o.status === 'submitted').length + " awaiting approval"} />
                            <StatCard icon={CalendarClock} label="Next invoice due" value={nextDue ? new Date(nextDue.due_date).toLocaleDateString() : "—"} sub={nextDue ? inr(nextDue.amount) + " · " + nextDue.invoice_no : "No issued invoices"} />
                        </div>

                        {/* v2G — Quick Reorder widget */}
                        <B2BQuickReorder />

                        <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft">
                            <div className="flex items-center justify-between">
                                <h3 className="text-base font-semibold text-zinc-900">Recent orders</h3>
                                <Link to="/business/orders" className="text-xs text-brand-700 hover:underline">View all →</Link>
                            </div>
                            {loading ? <div className="mt-4 h-24 skeleton rounded-xl" /> : orders.length === 0 ? (
                                <p className="mt-4 text-sm text-slate-500">No orders yet. <Link to="/business/shop" className="text-brand-700 hover:underline">Start shopping →</Link></p>
                            ) : (
                                <ul className="mt-4 divide-y divide-zinc-50">
                                    {orders.slice(0, 5).map((o) => (
                                        <li key={o.id} className="flex items-center justify-between py-3 text-sm" data-testid={`b2b-dash-order-${o.id}`}>
                                            <div>
                                                <Link to={`/business/orders/${o.id}`} className="font-mono text-zinc-900 hover:text-brand-700">{o.order_no}</Link>
                                                <p className="text-xs text-slate-500">{o.items?.length || 0} items · {(o.payment_terms || "").replace("_", " ")} · {new Date(o.created_at).toLocaleDateString()}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-zinc-900">{inr(o.amounts?.total)}</p>
                                                <p className="text-xs uppercase tracking-wider text-slate-500">{o.status.replace("_", " ")}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft">
                            <div className="flex items-center justify-between">
                                <h3 className="text-base font-semibold text-zinc-900">Recent invoices</h3>
                                <Link to="/business/invoices" className="text-xs text-brand-700 hover:underline">View all →</Link>
                            </div>
                            {invoices.length === 0 ? (
                                <p className="mt-4 text-sm text-slate-500">No invoices yet.</p>
                            ) : (
                                <ul className="mt-4 divide-y divide-zinc-50">
                                    {invoices.slice(0, 5).map((i) => (
                                        <li key={i.id} className="flex items-center justify-between py-3 text-sm" data-testid={`b2b-dash-invoice-${i.id}`}>
                                            <div>
                                                <p className="font-mono text-zinc-900">{i.invoice_no}</p>
                                                <p className="text-xs text-slate-500">Due {new Date(i.due_date).toLocaleDateString()} · {i.computed_status || i.status}</p>
                                            </div>
                                            <p className="font-semibold text-zinc-900">{inr(i.amount)}</p>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <Link to="/business/shop" className="btn-brand inline-flex !text-sm" data-testid="b2b-dash-shop-cta">
                            <ShoppingBag className="h-4 w-4" /> Open wholesale catalogue <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                );
            }}
        </B2BLayout>
    );
};

export default BusinessDashboard;
